"use client";

import { Alignment, Fit, Layout, useRive } from "@rive-app/react-canvas";
import { useEffect } from "react";

import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";

/** Default state machine name from the Rive editor. */
export const DEFAULT_RIVE_STATE_MACHINE = "State Machine 1";

interface RiveTileProps {
  src: string;
  className?: string;
  /** Accessible label describing the animation. */
  ariaLabel?: string;
  /**
   * State machine to play. Defaults to `null` (Rive's legacy first-timeline autoplay)
   * so arbitrary uploaded `.riv` files in the showcase grid keep working.
   * Pass a name (e.g. {@link DEFAULT_RIVE_STATE_MACHINE}) only for known assets.
   */
  stateMachines?: string | null;
  /**
   * When false, the instance stays mounted but is paused (useful for preloaded swaps).
   * Defaults to true.
   */
  active?: boolean;
}

/**
 * Renders a Rive (.riv) animation as a self-contained tile that fills its parent.
 * Used by the showcase grid for animated, non-game elements.
 */
export function RiveTile({
  src,
  className,
  ariaLabel,
  stateMachines = null,
  active = true,
}: RiveTileProps) {
  const overlayFrozen = useTheaterOverlayFrozen();
  const shouldPlay = active && !overlayFrozen;

  const { rive, RiveComponent } = useRive({
    src,
    // Always start playing so the first paint isn't blank; `active` then pauses extras.
    autoplay: true,
    ...(stateMachines ? { stateMachines } : {}),
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
  });

  useEffect(() => {
    if (!rive) return;
    if (shouldPlay) {
      // Resume only the configured state machine; bare play() starts every timeline.
      if (stateMachines) {
        rive.play(stateMachines);
      } else {
        rive.play();
      }
    } else {
      rive.pause();
    }
  }, [rive, shouldPlay, stateMachines]);

  return <RiveComponent className={className} aria-label={ariaLabel} role="img" />;
}
